/**
 * Macro execution contracts — the whole native playback surface.
 *
 * One profile (`playcast-visual256-v2`), one bundle, one measurement row, one
 * start command with a required playback asset grant, one capability profile.
 * Descriptors travel by reference: the bundle names `referencePages` and
 * `bindings`; Playjector fetches pages through the grant and verifies digests.
 */
import { z } from 'zod';
import { blobAssetSchema, evidenceCoverageSchema, macroVisualDigestSchema, VISUAL_ACCEPT_DENOMINATOR,
  VISUAL_ACCEPT_NUMERATOR, VISUAL_MARGIN_DENOMINATOR, VISUAL_MARGIN_NUMERATOR } from './macroCalibration';
import { MACRO_VISUAL_PROFILE, macroTreeGraphSchema, macroTriggerSchema } from './macroTree';
import { analysisTargetVariantSchema } from './macroCalibration/jobs';
import { sourceGraphIdentitySchema, validateSourceIdentity } from './macroPackage/workflow';

export const VISUAL_PLAYBACK_CONFIRM_FRAMES = 1;
export const VISUAL_PLAYBACK_MAX_FRAME_AGE_MS = 500;
/** Live diagnostic only: 0 permits any valid difference; 100 requires zero in the selected score mode. */
export const visualTestSensitivitySchema = z.number().int().min(0).max(100);
export const visualTestPreviewSettingsSchema = z.object({
  sensitivity: visualTestSensitivitySchema, rawScores: z.boolean(),
}).strict();
export type VisualTestPreviewSettings = z.infer<typeof visualTestPreviewSettingsSchema>;

/** `local-calibrated` sends input; `local-shadow` observes only and never sends input. */
export const macroRuntimeModeSchema = z.enum(['local-shadow', 'local-calibrated']);
export type MacroRuntimeMode = z.infer<typeof macroRuntimeModeSchema>;
export const DEFAULT_MACRO_RUNTIME_MODE: MacroRuntimeMode = 'local-calibrated';

const campaignId = z.string().min(1).max(128);
const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);
const boundedId = z.string().min(1).max(128);

/** Per-checkpoint threshold selected for one ephemeral visual-test run. */
export const visualTestThresholdOverrideSchema = z.object({
  nodeId: boundedId,
  matchThresholdPercent: z.number().int().min(0).max(100),
}).strict();
export const visualTestThresholdOverridesSchema = z.array(visualTestThresholdOverrideSchema).max(16_384);
export type VisualTestThresholdOverride = z.infer<typeof visualTestThresholdOverrideSchema>;
/**
 * Native calibration recommendation for one checkpoint. `poorQuality` is set when any bound reference
 * discarded too much benign training evidence (see `trainingQuality` on the calibration outcome).
 */
export const visualTestThresholdRecommendationSchema = visualTestThresholdOverrideSchema.extend({
  poorQuality: z.boolean().optional(),
}).strict();
export const visualTestThresholdRecommendationsSchema = z.array(visualTestThresholdRecommendationSchema).max(16_384);
export type VisualTestThresholdRecommendation = z.infer<typeof visualTestThresholdRecommendationSchema>;

/** Frozen rational bounds. Hamming thresholds are not accepted as this policy. */
export const visualPlaybackPolicySchema = z.object({
  acceptNumerator: z.literal(VISUAL_ACCEPT_NUMERATOR),
  acceptDenominator: z.literal(VISUAL_ACCEPT_DENOMINATOR),
  marginNumerator: z.literal(VISUAL_MARGIN_NUMERATOR),
  marginDenominator: z.literal(VISUAL_MARGIN_DENOMINATOR),
  confirmFrames: z.literal(VISUAL_PLAYBACK_CONFIRM_FRAMES),
  maxFrameAgeMs: z.literal(VISUAL_PLAYBACK_MAX_FRAME_AGE_MS),
}).strict();
export type VisualPlaybackPolicy = z.infer<typeof visualPlaybackPolicySchema>;

export const visualArtifactCertificationSchema = z.object({
  artifactDigest: macroVisualDigestSchema,
  deviceType: z.enum(['hardware', 'warp']),
  variant: analysisTargetVariantSchema,
  sourceIdentity: sourceGraphIdentitySchema.optional(),
  campaignId,
  trigger: macroTriggerSchema,
  rev: z.number().int().nonnegative(),
  expectedBindings: z.number().int().nonnegative().max(16_384),
  coveredBindings: z.number().int().nonnegative().max(16_384),
  readyBindings: z.number().int().nonnegative().max(16_384),
  generalizationCertified: z.boolean(),
  evidenceCoverage: evidenceCoverageSchema,
  /** Digest of the GPU kernel that produced the evidence; Playjector refuses to play against a different resident kernel. */
  kernelDigest: macroVisualDigestSchema,
}).strict().superRefine(validateSourceIdentity);
export type VisualArtifactCertification = z.infer<typeof visualArtifactCertificationSchema>;

/** Ties one tree snapshot to the calibrated reference row that describes it. */
export const playbackBindingSchema = z.object({
  nodeId: boundedId,
  snapshotId: boundedId,
  referenceId: boundedId,
  /** Set when this snapshot is one frame of a capture window. The anchor is 0. */
  offsetMs: z.number().int().min(-100).max(100).optional(),
}).strict();
export type PlaybackBinding = z.infer<typeof playbackBindingSchema>;

export const macroExecutionBundleSchema = z.object({
  campaignId,
  trigger: macroTriggerSchema,
  revision: sha256Hex,
  tree: macroTreeGraphSchema,
  profile: z.literal(MACRO_VISUAL_PROFILE),
  artifactDigest: macroVisualDigestSchema,
  certification: visualArtifactCertificationSchema,
  policy: visualPlaybackPolicySchema,
  referencePages: z.array(blobAssetSchema).min(1).max(512),
  bindings: z.array(playbackBindingSchema).min(1).max(16_384),
}).strict().superRefine((bundle, context) => {
  const certification = bundle.certification;
  if (bundle.artifactDigest !== certification.artifactDigest) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['artifactDigest'],
      message: 'Artifact certification does not match the bundle artifact' });
  }
  if (bundle.campaignId !== certification.campaignId) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['campaignId'],
      message: 'Artifact certification does not match the bundle campaign' });
  }
  if (bundle.trigger !== certification.trigger || bundle.trigger !== bundle.tree.trigger) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['trigger'],
      message: 'Bundle trigger must match the tree and the certification' });
  }
  const snapshots = new Set<string>();
  for (const node of bundle.tree.nodes) {
    for (const snapshot of node.expectedState?.snapshots ?? []) snapshots.add(node.id + ':' + snapshot.id);
  }
  bundle.bindings.forEach((binding, index) => {
    if (!snapshots.has(binding.nodeId + ':' + binding.snapshotId)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['bindings', index],
        message: 'Binding does not name a tree snapshot' });
    }
  });
});
export type MacroExecutionBundle = z.infer<typeof macroExecutionBundleSchema>;

/** Short-lived, scoped grant Playjector uses to fetch `referencePages`. */
export const playbackAssetGrantSchema = z.object({
  grantEndpoint: z.string().url().max(2048),
  token: z.string().min(1).max(4096),
  expiresAtUnixMs: z.number().int().positive(),
}).strict();
export type PlaybackAssetGrant = z.infer<typeof playbackAssetGrantSchema>;

/**
 * Live Detection: Playjector scores every frame its capture thread holds for the capture-UI-selected
 * source against the macro's checkpoints, with no launched process and no input. `continuous` watches
 * every checkpoint at once; `sequence` walks the authored tree without timing searches out.
 */
export const macroLiveDetectionSchema = z.enum(['continuous', 'sequence']);
export type MacroLiveDetection = z.infer<typeof macroLiveDetectionSchema>;

/** Capture-thread frame statistics for a Live Detection run (cumulative since start). */
export const macroLiveFrameStatsSchema = z.object({
  framesCaptured: z.number().int().nonnegative(),
  framesScored: z.number().int().nonnegative(),
  framesSkipped: z.number().int().nonnegative(),
}).strict();
export type MacroLiveFrameStats = z.infer<typeof macroLiveFrameStatsSchema>;

export const macroMeasurementSchema = z.object({
  nodeId: z.string().max(128),
  candidateId: z.string().max(128),
  frameSequence: z.number().int().nonnegative(),
  decision: z.enum(['no_match', 'ambiguous', 'pending', 'matched', 'stale', 'incomplete']),
  differenceNumerator: z.number().int().nonnegative(),
  differenceDenominator: z.number().int().positive(),
  runnerUpNumerator: z.number().int().nonnegative().nullable(),
  runnerUpDenominator: z.number().int().positive().nullable(),
  captureToDecisionMs: z.number(),
  rawEvidence: z.object({
    hammingPercent: z.number().min(0).max(100), imageDiffPercent: z.number().min(0).max(100),
    detailPercent: z.number().min(0).max(100).optional(),
    frequencyPercent: z.number().min(0).max(100).optional(),
    fusedPercent: z.number().min(0).max(100), hashUsed: z.boolean(), zeroBoundaryPercent: z.number().min(0).max(100).optional(),
  }).strict().optional(),
}).strict();
export type MacroMeasurement = z.infer<typeof macroMeasurementSchema>;

/** Last matched frame per node in a Live Detection run, retained between status polls. */
export const macroLatestMatchSchema = macroMeasurementSchema.extend({
  decision: z.literal('matched'),
  matchCount: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  eventSequence: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
});
export type MacroLatestMatch = z.infer<typeof macroLatestMatchSchema>;

const latestMatchesSchema = z.array(macroLatestMatchSchema).max(16_384).superRefine((matches, context) => {
  const nodes = new Set<string>(), events = new Set<number>();
  matches.forEach((match, index) => {
    if (nodes.has(match.nodeId) || events.has(match.eventSequence))
      context.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: 'Latest matches require distinct nodes and events' });
    nodes.add(match.nodeId); events.add(match.eventSequence);
  });
});

/**
 * Every `errorCode` Playjector emits, grouped by the stage that produces it. The host
 * synthesizes `host_cancelled`, `native_reply_timeout`, `native_send_failed` and
 * `release_unconfirmed` itself; those never come from native.
 */
export const playbackErrorCodeSchema = z.enum([
  // dispatcher / preflight
  'invalid_command', 'native_run_busy_or_invalid', 'native_preflight_failed', 'process_not_ready',
  // bundle decode
  'bundle_schema_invalid', 'bundle_profile_mismatch', 'bundle_binding_unknown_node', 'bundle_reference_pages_missing',
  'playback_kernel_incompatible',
  // reference delivery
  'playback_asset_unavailable', 'playback_reference_digest_mismatch', 'playback_reference_budget_exceeded',
  'playback_reference_uncalibrated',
  // capture / recognition
  'playback_engine_unavailable', 'playback_capture_device_changed', 'playback_capture_timeout', 'playback_observation_overflow',
  'playback_capture_geometry_changed', 'playback_capture_color_unsupported', 'playback_capture_invalidated', 'node_deadline', 'run_timeout',
  // input execution
  'input_lease_unavailable', 'input_geometry_changed', 'input_failed', 'input_release_unconfirmed',
  'native_runtime_error',
  'run_not_found',
]);
export type PlaybackErrorCode = z.infer<typeof playbackErrorCodeSchema>;

export interface MacroRunIdentity { runId: string; revision: string }
export type MacroRunOutcome = 'running' | 'completed' | 'failed' | 'cancelled';

/** Last actual native capture observation; omitted before capture and for action-only runs. */
export const macroCaptureTelemetrySchema = z.object({
  backend: z.enum(['dxgi', 'wgc']),
  textureFormat: z.number().int().nonnegative().max(0xffff_ffff),
  textureFormatName: z.enum(['R8G8B8A8_UNORM', 'R8G8B8A8_UNORM_SRGB', 'B8G8R8A8_UNORM',
    'B8G8R8A8_UNORM_SRGB', 'R10G10B10A2_UNORM', 'unrecognized']),
  outputBits: z.number().int().nonnegative().max(0xffff_ffff),
  colorStatus: z.enum(['unknown', 'pending', 'sdr_eligible', 'unsupported']),
  generation: z.number().int().positive(),
  frameSequence: z.number().int().nonnegative(),
  capturedAt: z.number().int().nonnegative(),
  width: z.number().int().positive(), height: z.number().int().positive(),
}).strict().superRefine((sample, context) => {
  const formats: Record<number, string> = { 24: 'R10G10B10A2_UNORM', 28: 'R8G8B8A8_UNORM', 29: 'R8G8B8A8_UNORM_SRGB',
    87: 'B8G8R8A8_UNORM', 91: 'B8G8R8A8_UNORM_SRGB' };
  if (sample.textureFormatName !== (formats[sample.textureFormat] ?? 'unrecognized'))
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['textureFormatName'], message: 'Format name differs from the observed DXGI code' });
  if (sample.colorStatus === 'sdr_eligible' && sample.outputBits !== 8 && sample.outputBits !== 10)
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['outputBits'], message: 'Eligible SDR requires output depth 8 or 10' });
});
export type MacroCaptureTelemetry = z.infer<typeof macroCaptureTelemetrySchema>;

export const macroRunReplySchema = z.object({
  requestId: boundedId, runId: z.string().max(128),
  revision: z.string().max(64),
  outcome: z.enum(['capabilities', 'running', 'completed', 'failed', 'cancelled', 'validated']),
  inputsSent: z.boolean(), errorCode: z.string().max(256).optional(),
  preflightStage: z.enum(['command', 'process_ready', 'capture_source', 'bundle']).optional(),
  measurements: z.array(macroMeasurementSchema).max(16_384).optional(),
  // Live Detection only: current scores can change before the next poll, so retain each node's last hit.
  latestMatches: latestMatchesSchema.optional(),
  capture: macroCaptureTelemetrySchema.optional(),
  previewSensitivity: visualTestSensitivitySchema.optional(),
  previewScoreMode: z.enum(['raw', 'calibrated']).optional(),
  liveFrameStats: macroLiveFrameStatsSchema.optional(),
  phase: z.enum(['idle', 'loading', 'observing', 'executing', 'settling', 'completed', 'failed', 'cancelled']).optional(),
  pendingObservations: z.number().int().nonnegative().max(64).optional(),
  timing: z.object({ p50Us: z.number().int(), p99Us: z.number().int(), maxUs: z.number().int(),
    samples: z.number().int() }).strict().optional(),
  // Native visual-test status replies include the shared runtime occupancy count.
  // Keep it in the base reply contract so local-shadow status is parseable too;
  // macroPackageRunReplySchema narrows/extends the same field for package runs.
  activeSessions: z.number().int().min(0).max(8).optional(),
  profiles: z.tuple([z.literal(MACRO_VISUAL_PROFILE)]).optional(),
  modes: z.array(macroRuntimeModeSchema).max(8).optional(),
}).strict();
export type MacroRunReply = z.infer<typeof macroRunReplySchema>;

export const macroRunCommandSchema = z.discriminatedUnion('command', [
  z.object({ command: z.literal('capabilities'), requestId: boundedId, runId: z.literal('') }).strict(),
  z.object({ command: z.literal('start'), requestId: boundedId, runId: boundedId, mode: macroRuntimeModeSchema,
    bundle: macroExecutionBundleSchema, assets: playbackAssetGrantSchema,
    liveDetection: macroLiveDetectionSchema.optional() }).strict(),
  z.object({ command: z.literal('cancel'), requestId: boundedId, runId: boundedId }).strict(),
  z.object({ command: z.literal('status'), requestId: boundedId, runId: boundedId }).strict(),
  z.object({ command: z.literal('preview-sensitivity'), requestId: boundedId, runId: boundedId,
    sensitivity: visualTestSensitivitySchema, rawScores: z.boolean() }).strict(),
]).superRefine((command, context) => {
  if (command.command !== 'start') return;
  if (command.liveDetection && command.mode !== 'local-shadow') {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Live Detection is observation-only' });
  }
});
export type MacroRunCommand = z.infer<typeof macroRunCommandSchema>;
